import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { svg } from '../assets/svg';
import { components } from '../components';
import Enums from '../helpers/enums';
import utils from '../helpers/utils';
import { hooks } from '../hooks';
import '../scss/component/_customization.scss';
import { CustomizeItemType } from '../types/CustomizeItemType';

interface Props {
    customizeItems: CustomizeItemType[];
    onApply: (items: CustomizeItemType[], totalCost: number) => void;
    selectedCustomizations: CustomizeItemType[];
    freeModifiers: number;
    isMandatory: boolean;
    isUpdateCart: boolean;
}

const CustomizeItem: React.FC<Props> = ({
    customizeItems: allCustomizations,
    onApply,
    selectedCustomizations,
    freeModifiers,
    isMandatory,
    isUpdateCart
}) => {
    const [customizeItems, setCustomizeItems] = useState<CustomizeItemType[]>([]);
    const [loading, setIsLoading] = useState(true);
    const [remainingFreeItem, setRemainingFreeItem] = useState(freeModifiers);
    const [disabled, setDisabled] = useState<boolean>(true);
    hooks.useScrollToTop();
    hooks.useScrollToTop();

    const disabledModifiers = useSelector((state: any) => {
        if (state.AppReducer) {
            return state.AppReducer.disabledModifiers;
        }
        return [];
    });

    useEffect(() => {
        let cloned = _.cloneDeep(allCustomizations);
        const firstExtraIndex = cloned.findIndex((item) => item.quantity === 0);
        if (firstExtraIndex !== -1) {
            cloned[firstExtraIndex].isFirstOccurrence = true;
        }

        cloned = _.differenceBy(cloned, disabledModifiers ?? [], 'posItemId');
        let totalFreeItems = remainingFreeItem;
        _.map(selectedCustomizations, (selectedItem) => {
            const idx = cloned.findIndex(
                (item) => item.posItemId === selectedItem.posItemId && item.setId === selectedItem.setId
            );
            if (idx >= 0) {
                const filterItem = cloned[idx];
                filterItem.modCodeId = selectedItem.modCodeId;
                filterItem.quantity = selectedItem.quantity;
                if (freeModifiers && filterItem.setId === 1) {
                    filterItem.freeQty = Math.min(totalFreeItems, selectedItem.quantity);
                    totalFreeItems -= filterItem.freeQty;
                }
            }
        });

        setRemainingFreeItem(totalFreeItems);
        setCustomizeItems(cloned);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!isMandatory) {
            setDisabled(false);
            return;
        }

        const mandatoryGroups = _.groupBy(customizeItems, "setId");
        let allMandatoryValid = true;

        Object.entries(mandatoryGroups).forEach(([_, items]) => {
            const mandatoryItems = items.filter((i) => i.isMandatory);
            if (mandatoryItems.length > 0) {
                const hasSelected = mandatoryItems.some((i) => i.quantity > 0);
                if (!hasSelected) {
                    allMandatoryValid = false;
                }
            }
        });

        setDisabled(!allMandatoryValid);
    }, [customizeItems, isMandatory]);

    const { customizeItemsMemoized, totalCustomizeItemsCost } = useMemo(() => {
        let totalFreeItems = remainingFreeItem;

        const updated = customizeItems.map((item) => {
            const newItem = { ...item, label: '', modCodeId: -1 };
            if (item.defaultQuantity !== item.quantity) {
                if (item.isMandatory) {
                    if (item.quantity > 0) {
                        newItem.label = `Selected ${item.name} (${Enums.ModifiersSetDisplay[item.setId as keyof typeof Enums.ModifiersSetDisplay] ?? ''})`;
                        newItem.modCodeId = 1;
                    }
                    return newItem;
                }
                if (item.defaultQuantity > item.quantity) {
                    newItem.label = `Removed ${item.name}`;
                    newItem.modCodeId = 2;
                } else if (item.defaultQuantity < item.quantity) {
                    newItem.modCodeId = 1;
                    if (item.setId === Enums.ModifiersSet.PacketSauce && totalFreeItems > 0 &&
                        item.price > 0) {
                        newItem.freeQty = Math.min(totalFreeItems, item.quantity);
                        totalFreeItems = totalFreeItems - newItem.freeQty;
                    }
                    if (newItem && newItem.freeQty > 0) {
                        if (newItem.freeQty === item.quantity) {
                            newItem.price = 0;
                            newItem.label = `${item.quantity} X Added ${item.name} free`;
                        }
                        else {
                            const newQty = item.quantity - newItem.freeQty;
                            newItem.price = newQty * newItem.price;
                            newItem.label = `${item.quantity} X Added ${item.name} +${utils.formatCurrency(item.price * newQty)}`;
                        }
                    } else {
                        const newQty = item.quantity - item.defaultQuantity;
                        newItem.price = newQty * item.price;
                        newItem.label = `${newQty} X Added ${item.name} +${utils.formatCurrency(item.price * newQty)}`;
                    }
                }
            }
            return newItem;
        });

        const totalCost = updated.reduce((total, item) => {
            return total + (item.modCodeId === 1 && item.price > 0 ? item.price : 0);
        }, 0);

        return { customizeItemsMemoized: updated, totalCustomizeItemsCost: totalCost };
    }, [customizeItems]);

    const handleQtyChange = (posItemId: number, newValue: number) => {
        const updatedCustomizeItems = [...customizeItems];
        const itemIndex = updatedCustomizeItems.findIndex(
            (item) => item.posItemId === posItemId
        );
        if (itemIndex !== -1) {
            updatedCustomizeItems[itemIndex] = {
                ...updatedCustomizeItems[itemIndex],
                quantity: Math.max(0, newValue)
            };
            setCustomizeItems(updatedCustomizeItems);
        }
    };

    const groupedMandatoryItems = useMemo(() => {
        const groups: Record<number, CustomizeItemType[]> = {};
        customizeItems.forEach((item) => {
            if (item.isMandatory) {
                if (!groups[item.setId]) {
                    groups[item.setId] = [];
                }
                groups[item.setId].push(item);
            }
        });
        return groups;
    }, [customizeItems]);

    const renderMandatory = (): JSX.Element | null => {
        if (!Object.keys(groupedMandatoryItems).length) return null;
        return (
            <div className="mandatory-section" style={{ paddingLeft: 18, paddingTop: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', paddingBottom: 10, borderRadius: '0px 0px 20px 20px' }}>
                {Object.entries(groupedMandatoryItems).map(([setId, items]: any) => (
                    <div key={setId} className="mandatory-group">
                        <h4 className='t16'>{Enums.ModifiersSetDisplay[setId as keyof typeof Enums.ModifiersSetDisplay] ?? "Mandatory Options"}</h4>
                        <div style={{ display: 'inline-flex', gap: '15px', marginTop: 5, marginBottom: 5, flexWrap: 'wrap' }}>
                            {items.map((item: any) => (
                                <button
                                    key={item.posItemId}
                                    onClick={() => handleMandatoryChange(item.posItemId, Number(setId))}
                                    style={{
                                        padding: "6px 10px",
                                        justifyContent: "center",
                                        borderRadius: "15px",
                                        border: "none",
                                        background:
                                            item.quantity > 0 ? "var(--main-turquoise)" : "#ffffff",
                                        color: item.quantity > 0 ? "#fff" : "#333",
                                        fontWeight: item.quantity > 0 ? 600 : 400,
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        boxShadow: "0 2px 0px rgba(0,0,0,0.15)",
                                    }}
                                    className="t12"
                                >
                                    {item.name}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleMandatoryChange = (posItemId: number, setId: number) => {
        const updated = customizeItems.map((item) => {
            if (item.isMandatory && item.setId === setId) {
                return { ...item, quantity: item.posItemId === posItemId ? 1 : 0 };
            }
            return item;
        });

        setCustomizeItems(updated);
        setDisabled(false);
    };

    const handleApply = () => {
        onApply(customizeItemsMemoized, totalCustomizeItemsCost);
    };

    const renderCustomization = (): JSX.Element => {
        return (

            <main className='scrollable'>
                {loading ? (
                    <div className='spinner'>Loading...</div>
                ) : (
                    <div style={{ marginTop: 6 }}>
                        {customizeItems.filter((i: any) => !i.isMandatory)
                            .map((item, index) => {
                                let maxValue = 0, minValue = 0;
                                const maxQtyAllowed = 3;
                                if (item.canRemove === 1 && item.canAdd === 1) {
                                    maxValue = maxQtyAllowed + 1;
                                    minValue = 0;
                                }
                                else if (item.canRemove === 0 && item.canAdd === 1) {
                                    maxValue = maxQtyAllowed;
                                    minValue = 0;
                                }
                                else if (item.canRemove === 1 && item.canAdd === 0) {
                                    maxValue = 1;
                                    minValue = 0;
                                }
                                item.quantity = item.quantity ?? 0;
                                if (freeModifiers) {
                                    maxValue = maxValue + 4;
                                }
                                return (
                                    <div className='cj-customize-item' key={item.posItemId}>
                                        {item.isFirstOccurrence && (
                                            <div style={{
                                                borderRadius: 10,
                                                display: 'flex',
                                                padding: 8,
                                                marginLeft: 10,
                                                alignItems: 'start',
                                                flexDirection: 'column',
                                                marginTop: 8,
                                                position: 'relative',
                                            }}
                                            >
                                                <h4>Add Extras</h4>
                                                {freeModifiers > 0 && (
                                                    <p className='t12'>{`Choose ${freeModifiers} free sauces; extras will be charged.`}</p>
                                                )}
                                            </div>
                                        )}
                                        <div key={item.posItemId} className='item-row'>
                                            <div className='item-left'>
                                                <img
                                                    src={utils.getImagePath(item.imagePath)}
                                                    alt={item.name}
                                                    className='item-image'
                                                />
                                                <div className='item-left' style={{ maxWidth: '100%' }}>
                                                    <span className='t14 number-of-lines-2'>{item.name}</span>
                                                </div>
                                            </div>
                                            <div className='row-center'>
                                                {item.quantity > 0 && item.defaultQuantity !== item.quantity &&
                                                    <div>
                                                        <span className='t14'>{utils.formatCurrency(item.price)}</span>
                                                    </div>
                                                }
                                                <button
                                                    style={{
                                                        padding: '14px 14px 14px 14px', borderRadius: 4
                                                    }}
                                                    onClick={() => {
                                                        handleQtyChange(item.posItemId, item.quantity - 1)
                                                    }}
                                                    disabled={item.quantity <= minValue}
                                                >
                                                    <svg.RemoveSvg />
                                                </button>
                                                <div style={{ marginLeft: 4, marginRight: 4 }}>
                                                    <span
                                                        className='t14'
                                                        style={{ fontWeight: 700, color: 'var(--text-color)' }}
                                                    >
                                                        <span className='t14'>{item.quantity}</span>
                                                    </span>
                                                </div>

                                                <button
                                                    style={{ padding: '14px 14px 14px 14px', borderRadius: 4 }}
                                                    onClick={() => {
                                                        handleQtyChange(item.posItemId, item.quantity + 1)
                                                    }}
                                                    disabled={item.quantity >= maxValue}
                                                >
                                                    <svg.AddSvg />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                    </div>
                )}
            </main>
        );
    };

    return (
        <>
            {isMandatory ? renderMandatory() : null}
            {renderCustomization()}
            <div className='summary'>
                <div className='custom-items-list'>
                    {customizeItemsMemoized
                        .filter((i) => i.label)
                        .map((customItem, idx) => (
                            <p key={idx} className='t14 summary-item'
                                style={{ color: customItem.label.includes('Removed') ? 'var(--main-turquoise)' : 'var(--main-color)' }}>
                                {customItem.label}
                            </p>
                        ))}
                </div>
                <div style={{ margin: 20 }}>
                    <components.Button
                        disabled={disabled}
                        text={isMandatory && isUpdateCart ? 'Update Cart' : isMandatory ? 'Add to Cart' : 'Apply Customisation'}
                        onClick={() => handleApply()}
                    />
                </div>
            </div>
        </>
    );
};

export default CustomizeItem;
